
import { Icon } from '@/components/ui/Icon'
import { SLOT_BUSES, SLOT_BUS_LABELS, type SlotBus } from '@/lib/categories'
import { describeDevice } from '@/lib/signals'
import { btn, cx, iconBtn, input, inputBase } from '@/lib/ui'
import { splitPorts, type DeviceDTO, type DeviceSlotDTO } from '@/lib/types'

export type EditableSlot = {
  key: string
  /** Present when the slot already exists; keeps its copied ports across saves. */
  id?: string
  label: string
  bus: SlotBus
  cardId: string | null
}

const newKey = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2)

/**
 * The slot list on a computer: label, bus and the installed card. Card ports
 * are shown as a read-only summary only — they are copied server-side when
 * the computer is saved, never edited here.
 */
export function SlotsEditor({
  slots,
  cards,
  existingSlots,
  onChange,
}: {
  slots: EditableSlot[]
  /** Capture cards this computer may install. */
  cards: DeviceDTO[]
  /** The computer's slots as saved, to tell an unchanged card from a swap. */
  existingSlots: DeviceSlotDTO[]
  onChange: (slots: EditableSlot[]) => void
}) {
  const cardById = new Map(cards.map((card) => [card.id, card]))
  const existingById = new Map(existingSlots.map((slot) => [slot.id, slot]))
  const catalogue = cards.filter((card) => card.workspaceId === null)
  const ours = cards.filter((card) => card.workspaceId !== null)
  const filled = slots.filter((slot) => slot.cardId).length

  function update(key: string, patch: Partial<EditableSlot>) {
    onChange(slots.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot)))
  }

  function remove(key: string) {
    const slot = slots.find((candidate) => candidate.key === key)
    const card = slot?.cardId ? cardById.get(slot.cardId) : undefined
    if (
      slot &&
      card &&
      !confirm(
        `Removing ${slot.label} deletes its ${card.ports.length} ${card.model} port${card.ports.length === 1 ? '' : 's'} and any cables patched to them. Continue?`,
      )
    ) {
      return
    }
    onChange(slots.filter((candidate) => candidate.key !== key))
  }

  function add() {
    // First unused number, so removing a middle slot never mints a duplicate.
    const taken = new Set(slots.map((slot) => slot.label.trim().toLowerCase()))
    let n = slots.length + 1
    while (taken.has(`slot ${n}`)) n += 1
    onChange([...slots, { key: newKey(), label: `Slot ${n}`, bus: 'PCIE', cardId: null }])
  }

  const option = (card: DeviceDTO) => (
    <option key={card.id} value={card.id}>
      {describeDevice(card.manufacturer, card.model)} · {card.ports.length} port{card.ports.length === 1 ? '' : 's'}
    </option>
  )

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h3 className="text-base font-semibold tracking-tight">Slots</h3>
        <p className="font-mono text-[11px] text-muted">
          {filled} of {slots.length} filled
        </p>
      </div>

      {slots.length === 0 ? (
        <p className="mt-3 rounded-2xl border border-dashed border-[var(--border-strong)] px-3 py-8 text-center text-xs text-muted">
          No slots yet. Add one per PCIe or Thunderbolt bay, then choose the card installed in it.
        </p>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {slots.map((slot) => {
            const card = slot.cardId ? cardById.get(slot.cardId) : undefined
            const saved = slot.id ? existingById.get(slot.id) : undefined
            const unchanged = saved !== undefined && saved.cardId === slot.cardId
            const counts = card ? splitPorts(card.ports) : null
            return (
              <li key={slot.key} className="rounded-xl border border-[var(--border)] bg-surface p-2 shadow-sm">
                <div className="grid items-center gap-2 sm:grid-cols-[7rem_auto_1fr_auto]">
                  <input
                    className={input}
                    value={slot.label}
                    onChange={(event) => update(slot.key, { label: event.target.value })}
                    aria-label="Slot label"
                    maxLength={40}
                  />
                  <select
                    className={inputBase}
                    value={slot.bus}
                    onChange={(event) => update(slot.key, { bus: event.target.value as SlotBus })}
                    aria-label="Slot bus"
                  >
                    {SLOT_BUSES.map((bus) => (
                      <option key={bus} value={bus}>
                        {SLOT_BUS_LABELS[bus]}
                      </option>
                    ))}
                  </select>
                  <select
                    className={input}
                    value={slot.cardId ?? ''}
                    onChange={(event) => update(slot.key, { cardId: event.target.value || null })}
                    aria-label="Installed card"
                  >
                    <option value="">Empty</option>
                    {catalogue.length > 0 && <optgroup label="Catalogue">{catalogue.map(option)}</optgroup>}
                    {ours.length > 0 && <optgroup label="Ours">{ours.map(option)}</optgroup>}
                  </select>
                  <button
                    type="button"
                    onClick={() => remove(slot.key)}
                    aria-label={`Remove ${slot.label}`}
                    className={cx(iconBtn, 'hover:bg-danger-soft hover:text-danger')}
                  >
                    <Icon name="close" className="h-4 w-4" />
                  </button>
                </div>
                {card && counts && (
                  <p className="mt-1.5 px-1 font-mono text-[10px] text-muted">
                    {card.ports.length} port{card.ports.length === 1 ? '' : 's'} from {card.model}: {counts.inputs.length} in ·{' '}
                    {counts.outputs.length} out
                    {counts.bidirectional.length > 0 ? ` · ${counts.bidirectional.length} bidi` : ''} ·{' '}
                    {unchanged ? 'cables kept on save' : 'ports copied on save'}
                  </p>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={add} className={cx(btn.base, btn.secondary)}>
          <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
          Add slot
        </button>
        <p className="text-[11px] text-muted">
          Changing a slot&apos;s card replaces its ports; cables patched to them are removed.
        </p>
      </div>
    </section>
  )
}
